import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Badge } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getInternalAlerts, updateInternalAlerts, sendInternalAlertTest } from '../../../services/SettingsService';

const EMAIL_RE = /^[^@\s,;<>"'()]+@[^@\s,;<>"'()]+\.[^@\s,;<>"'()]+$/;
const MAX_RECIPIENTS = 20;

export default function InternalOrderAlerts() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [initial, setInitial] = useState({ enabled: false, recipients: [], subject_template: '' });
    const [defaultSubject, setDefaultSubject] = useState('');
    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');

    const apply = (data) => {
        setDefaultSubject(data.default_subject || '');
        setInitial({
            enabled: !!data.enabled,
            recipients: data.recipients || [],
            subject_template: data.subject_template || data.default_subject || '',
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await getInternalAlerts();
                apply(res.data || {});
            } catch {
                toast.error(t('internalAlerts.errors.load'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const schema = Yup.object({
        enabled: Yup.boolean(),
        recipients: Yup.array().of(Yup.string()).max(MAX_RECIPIENTS, t('internalAlerts.errors.tooMany')),
        subject_template: Yup.string().trim()
            .required(t('internalAlerts.errors.subjectRequired'))
            .max(250, t('internalAlerts.errors.subjectTooLong')),
    });

    const handleSave = async (values, { setSubmitting }) => {
        try {
            const res = await updateInternalAlerts({
                enabled: values.enabled,
                recipients: values.recipients,
                subject_template: values.subject_template.trim() === defaultSubject ? '' : values.subject_template.trim(),
            });
            apply(res.data);
            toast.success(t('internalAlerts.success.saved'));
        } catch (err) {
            toast.error(err.response?.data?.error || t('internalAlerts.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const res = await sendInternalAlertTest();
            toast.success(t('internalAlerts.success.testSent', { to: (res.data.sent || []).join(', ') }));
        } catch (err) {
            toast.error(err.response?.data?.error || t('internalAlerts.errors.testFailed'));
        } finally {
            setTesting(false);
        }
    };

    const addDraft = (values, setFieldValue) => {
        const email = draft.trim().replace(/,$/, '').toLowerCase();
        if (!email) return;
        if (!EMAIL_RE.test(email) || email.length > 200) {
            setDraftError(t('internalAlerts.errors.invalidEmail'));
        } else if (values.recipients.includes(email)) {
            setDraftError(t('internalAlerts.errors.duplicate'));
        } else if (values.recipients.length >= MAX_RECIPIENTS) {
            setDraftError(t('internalAlerts.errors.tooMany'));
        } else {
            setFieldValue('recipients', [...values.recipients, email]);
            setDraft('');
            setDraftError('');
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <div>
                                <h4 className="heading mb-0">{t('internalAlerts.title')}</h4>
                                <span className="text-muted">{t('internalAlerts.subtitle')}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : (
                                <Formik initialValues={initial} validationSchema={schema} onSubmit={handleSave} enableReinitialize>
                                    {({ values, errors, handleSubmit, setFieldValue, handleChange, isSubmitting }) => (
                                        <Form noValidate onSubmit={handleSubmit}>
                                            <Form.Check
                                                type="switch"
                                                id="internal-alerts-enabled"
                                                name="enabled"
                                                className="mb-1"
                                                label={t('internalAlerts.enabledLabel')}
                                                checked={values.enabled}
                                                onChange={handleChange}
                                            />
                                            <p className="text-muted small mb-4">{t('internalAlerts.enabledHint')}</p>

                                            <Form.Group className="mb-4" controlId="internal-alerts-recipient">
                                                <Form.Label>{t('internalAlerts.recipientsLabel')}</Form.Label>
                                                <div className="d-flex flex-wrap gap-2 mb-2" data-testid="internal-alert-recipients">
                                                    {values.recipients.map((email) => (
                                                        <Badge key={email} bg="light" text="dark" className="border d-flex align-items-center gap-2 fs-6 fw-normal">
                                                            {email}
                                                            <button
                                                                type="button"
                                                                className="btn-close btn-close-sm"
                                                                aria-label={t('internalAlerts.removeRecipient', { email })}
                                                                onClick={() => setFieldValue('recipients', values.recipients.filter((r) => r !== email))}
                                                            />
                                                        </Badge>
                                                    ))}
                                                </div>
                                                {values.recipients.length === 0 && (
                                                    <p className="text-muted small mb-2">{t('internalAlerts.recipientsEmpty')}</p>
                                                )}
                                                <div className="d-flex gap-2 internal-alerts-add">
                                                    <Form.Control
                                                        type="email"
                                                        value={draft}
                                                        isInvalid={!!draftError}
                                                        placeholder={t('internalAlerts.recipientsPlaceholder')}
                                                        onChange={(e) => { setDraft(e.target.value); setDraftError(''); }}
                                                        onBlur={() => draft.trim() && addDraft(values, setFieldValue)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ',') {
                                                                e.preventDefault();
                                                                addDraft(values, setFieldValue);
                                                            }
                                                        }}
                                                    />
                                                    <Button variant="outline-secondary" onClick={() => addDraft(values, setFieldValue)}>
                                                        {t('internalAlerts.addButton')}
                                                    </Button>
                                                </div>
                                                {draftError && <div className="text-danger small mt-1">{draftError}</div>}
                                                <Form.Text className="text-muted">{t('internalAlerts.recipientsHint')}</Form.Text>
                                                {errors.recipients && <div className="text-danger small">{errors.recipients}</div>}
                                            </Form.Group>

                                            <Form.Group className="mb-4" controlId="internal-alerts-subject">
                                                <Form.Label>{t('internalAlerts.subjectLabel')}</Form.Label>
                                                <Form.Control
                                                    name="subject_template"
                                                    value={values.subject_template}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.subject_template}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.subject_template}</Form.Control.Feedback>
                                                <Form.Text className="text-muted d-block">{t('internalAlerts.subjectHint')}</Form.Text>
                                                {values.subject_template !== defaultSubject && (
                                                    <Button variant="link" size="sm" className="p-0" onClick={() => setFieldValue('subject_template', defaultSubject)}>
                                                        {t('internalAlerts.resetSubject')}
                                                    </Button>
                                                )}
                                            </Form.Group>

                                            <Button type="submit" variant="primary" disabled={isSubmitting} className="me-3">
                                                {isSubmitting ? t('common.saving') : t('common.save')}
                                            </Button>

                                            <hr className="my-4" />

                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <Button variant="outline-primary" onClick={handleTest} disabled={testing}>
                                                    {testing ? t('internalAlerts.sendingTest') : t('internalAlerts.sendTestButton')}
                                                </Button>
                                                <span className="text-muted small">{t('internalAlerts.testHint')}</span>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
